import React, { useState, useEffect, Suspense, lazy, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import '../styles/ParameterChoice.css';

const TagsMatching = lazy(() => import('./TagsMatching'));
const Distance     = lazy(() => import('./Distance'));

const ParameterChoice = () => {
  const { user_id } = useParams();
  const navigate    = useNavigate();

  const [activeStep, setActiveStep]         = useState(0);
  const [accountStatus, setAccountStatus]   = useState(null);
  const [selectedVenues, setSelectedVenues] = useState([]);
  const [tagFilteredVenues, setTagFilteredVenues] = useState([]);
  const [storageAccessNeeded, setStorageAccessNeeded] = useState(false);

  const stepsCount         = 3;
  const progressPercentage = ((activeStep + 1) / stepsCount) * 100;

  const fetchAccountStatus = useCallback(() => {
    axios.get(
      'https://api.bestday2marry.com/account_status/',
      {
        params: { user_id },
        withCredentials: true
      }
    )
    .then(res => {
      if (res.data.status === 'success') {
        setAccountStatus(res.data.account_status);
      } else {
        console.error('Error fetching account status:', res.data.message);
      }
    })
    .catch(err => {
      console.error('Error during fetch:', err);
    });
  }, [user_id]);

  useEffect(() => {
    // Storage Access API check (unchanged)
    const hasSA = typeof document.requestStorageAccess === 'function' &&
                  typeof document.hasStorageAccess === 'function';

    if (hasSA) {
      document.hasStorageAccess()
        .then(has => {
          if (has) fetchAccountStatus();
          else setStorageAccessNeeded(true);
        })
        .catch(() => {
          setStorageAccessNeeded(true);
        });
    } else {
      if (navigator.userAgent.includes("Safari") && !navigator.userAgent.includes("Chrome")) {
        alert("Please enable cookies/cross-site tracking in Safari settings.");
      }
      fetchAccountStatus();
    }
  }, [fetchAccountStatus]);

  const handleRequestStorageAccess = () => {
    if (typeof document.requestStorageAccess === 'function') {
      document.requestStorageAccess()
        .then(() => {
          setStorageAccessNeeded(false);
          fetchAccountStatus();
        })
        .catch(() => {
          alert("Please enable storage access.");
        });
    } else {
      alert("Storage Access API not supported; please ensure cookies are enabled.");
    }
  };

  const handleNextStep = () => {
    if (activeStep === 0) {
      if (tagFilteredVenues.length === 0) {
        alert("Select some tags first.");
        return;
      }
      setActiveStep(1);
    } else if (activeStep === 1) {
      if (selectedVenues.length === 0) {
        alert("Select at least one venue.");
        return;
      }
      navigate(`/preferences/${user_id}`, { state: { selectedVenues } });
    }
  };

  const handleBack = () => {
    if (activeStep === 1) setActiveStep(0);
  };

  return (
    <div className="parameter-choice-page-container">
      <header className="logo">
        <a href="/">BestDay2Marry</a>
      </header>

      {storageAccessNeeded && (
        <div className="storage-access-request">
          <p>Enable storage access for best experience:</p>
          <button onClick={handleRequestStorageAccess}>Enable Storage Access</button>
        </div>
      )}

      <div className="parameter-choice-container">
        <div className="progress-bar-container">
          <div
            className="progress-bar-fill"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>

        <Suspense fallback={<div>Loading step...</div>}>
          {activeStep === 0 && (
            <TagsMatching
              user_id={user_id}
              accountStatus={accountStatus}
              selectedVenues={selectedVenues}
              setSelectedVenues={setSelectedVenues}
              setTagFilteredVenues={setTagFilteredVenues}
            />
          )}
          {activeStep === 1 && (
            <Distance
              user_id={user_id}
              accountStatus={accountStatus}
              selectedVenues={selectedVenues}
              setSelectedVenues={setSelectedVenues}
              tagFilteredVenues={tagFilteredVenues}
            />
          )}
        </Suspense>

        <div className="sticky-footer">
          {activeStep === 1 && (
            <button
              type="button"
              className="step-btn step-btn--secondary"
              onClick={handleBack}
            >
              Back
            </button>
          )}
          <button
            type="button"
            className="step-btn step-btn--primary"
            onClick={handleNextStep}
          >
            {activeStep === 0 ? 'Next: Distance' : 'Next: Preferences'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ParameterChoice;